import type { DataPoint } from '@/app/(client)/network/-types';
import { CompletionPanel } from '@/components/shared/CompletionPanel';
import type { SectionNode } from '@/components/shared/SectionTree';
import { SectionTree } from '@/components/shared/SectionTree';

export type { SectionNode } from '@/components/shared/SectionTree';

interface ProfileSidebarProps {
  title: string;
  rootLabel?: string;
  sections: SectionNode[];
  overallPct?: number | undefined;
  t: (key: string, params?: Record<string, string | number>) => string;
}

function isValidNumber(v: unknown): boolean {
  if (typeof v === 'number') return !Number.isNaN(v);
  if (typeof v === 'string' && v.trim() !== '') return !Number.isNaN(Number(v));
  return false;
}

const NUMBER_TYPES = new Set([
  'NUMBER',
  'NUMBER_UOM',
  'NUMBER_DYNAMIC_UOM',
  'NUMBER_DYNAMIC_CURRENCY',
  'NUMBER_DYNAMIC_UOM_WITH_YEAR',
  'NUMBER_DYNAMIC_CURRENCY_WITH_YEAR',
]);

function isDataPointFilled(dp: DataPoint): boolean {
  const v = dp.value;
  if (v === null || v === undefined) return false;

  if (dp.fieldTypeId === 'TABLE') {
    if (!Array.isArray(v) || v.length === 0) return false;
    const firstRow = v[0] as Record<string, unknown>;
    return Object.values(firstRow).every(cell => {
      if (cell === null || cell === undefined) return false;
      if (typeof cell === 'object' && !Array.isArray(cell)) {
        return isValidNumber((cell as Record<string, unknown>).value);
      }
      return true;
    });
  }

  if (dp.fieldTypeId === 'CHECK_BOX' || dp.fieldTypeId === 'MULTI_SELECT') {
    return (
      Array.isArray(v) && v.some(item => item !== null && item !== undefined)
    );
  }

  if (dp.fieldTypeId === 'NUMBER') return isValidNumber(v);

  if (dp.fieldTypeId && NUMBER_TYPES.has(dp.fieldTypeId)) {
    if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
      return isValidNumber((v as Record<string, unknown>).value);
    }
    return isValidNumber(v);
  }

  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

export function countDataPoints(data: Record<string, DataPoint>): {
  filled: number;
  total: number;
} {
  const entries = Object.values(data).filter(dp => dp.isVisible !== false);
  return {
    total: entries.length,
    filled: entries.filter(isDataPointFilled).length,
  };
}

function sumCounts(nodes: SectionNode[]): { filled: number; total: number } {
  return nodes.reduce(
    (acc, node) => {
      if (node.children?.length) {
        const sub = sumCounts(node.children);
        return {
          filled: acc.filled + sub.filled,
          total: acc.total + sub.total,
        };
      }
      return {
        filled: acc.filled + (node.filled ?? 0),
        total: acc.total + (node.total ?? 0),
      };
    },
    { filled: 0, total: 0 },
  );
}

export function ProfileSidebar({
  title,
  rootLabel,
  sections,
  overallPct,
  t,
}: ProfileSidebarProps) {
  const { filled, total } = sumCounts(sections);
  const calculatedPct = total > 0 ? Math.round((filled / total) * 100) : 0;
  const pct = overallPct ?? calculatedPct;

  return (
    <CompletionPanel
      title={title}
      pct={pct}
      fillLabel={t('generalInfo.fieldsFilled', { filled, total })}
    >
      <SectionTree
        sections={sections}
        {...(rootLabel !== undefined ? { rootLabel } : {})}
      />
    </CompletionPanel>
  );
}
